import { useEffect, useState } from 'react'
import { BlastAtmosphere } from '../components/BlastAtmosphere'
import { EmptyState, ErrorState, LoadingState } from '../components/Feedback'
import { Pagination } from '../components/Pagination'
import { PlayerAvatar } from '../components/PlayerAvatar'
import { RankBadge } from '../components/RankBadge'
import { api } from '../lib/api'
import { number, percentage } from '../lib/format'
import { Link, useSearchParams } from '../lib/router'
import type { PlayerRef } from '../lib/types'
import { useApi } from '../lib/useApi'

export function LeaderboardPage() {
  const [params, setParams] = useSearchParams()
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1)
  const [search, setSearch] = useState('')
  const [searchState, setSearchState] = useState<{
    query: string
    results: PlayerRef[]
    loading: boolean
  }>({ query: '', results: [], loading: false })
  const { data, loading, error } = useApi(`leaderboard-${page}`, (signal) =>
    api.leaderboard(page, 25, signal),
  )

  useEffect(() => {
    const query = search.trim()
    if (query.length < 2) return
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      setSearchState({ query, results: [], loading: true })
      api
        .searchPlayers(query, controller.signal)
        .then((response) =>
          setSearchState({ query, results: response.data, loading: false }),
        )
        .catch(() =>
          setSearchState({ query, results: [], loading: false }),
        )
    }, 250)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [search])

  const entries = data?.data ?? []
  const top = page === 1 ? entries.slice(0, 3) : []
  const normalizedSearch = search.trim()
  const activeSearch =
    searchState.query === normalizedSearch
      ? searchState
      : { query: normalizedSearch, results: [], loading: false }

  return (
    <main className="section page page--charged page--leaderboard">
      <BlastAtmosphere variant="subtle" />
      <header className="page-heading page-heading--split">
        <div>
          <p className="eyebrow">Stagione {data?.meta.season.id ?? 'corrente'}</p>
          <h1>Leaderboard</h1>
          <p>Il ranking ufficiale TNTWars ordinato per MMR.</p>
        </div>
        <div className="player-search">
          <label htmlFor="player-search">Cerca giocatore</label>
          <input
            autoComplete="off"
            id="player-search"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Username Minecraft"
            type="search"
            value={search}
          />
          {search.trim().length >= 2 ? (
            <div className="search-results">
              {activeSearch.loading ? <span>Ricerca…</span> : null}
              {!activeSearch.loading && activeSearch.results.length === 0 ? (
                <span>Nessun giocatore trovato.</span>
              ) : null}
              {activeSearch.results.map((player) => (
                <Link key={player.uuid} to={`/player/${player.uuid}`}>
                  <PlayerAvatar
                    size={32}
                    username={player.username}
                    uuid={player.uuid}
                  />
                  {player.username}
                  <span>Apri →</span>
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      </header>

      {loading ? <LoadingState label="Sincronizzazione classifica" /> : null}
      {error ? <ErrorState /> : null}
      {!loading && !error && entries.length === 0 ? (
        <EmptyState
          message="Nessun giocatore ha ancora completato i placement di questa stagione."
          title="Classifica in attesa"
        />
      ) : null}

      {top.length > 0 ? (
        <section className="podium" aria-label="Podio">
          {top.map((entry, index) => (
            <Link
              className={`podium-card podium-card--${index + 1}`}
              key={entry.uuid}
              to={`/player/${entry.uuid}`}
            >
              <span className="podium-card__position">
                #{entry.rankPosition}
              </span>
              <div className="podium-card__avatar">
                <PlayerAvatar
                  size={index === 0 ? 128 : 96}
                  username={entry.username}
                  uuid={entry.uuid}
                />
              </div>
              <h2>{entry.username}</h2>
              <RankBadge rank={entry.rank} />
              <div className="podium-card__stats">
                <strong>{number(entry.mmr)} MMR</strong>
                <span>{percentage(entry.winRate)} win rate</span>
              </div>
            </Link>
          ))}
        </section>
      ) : null}

      {entries.length > 0 && data ? (
        <section className="leaderboard-table-wrap">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th>Posizione</th>
                <th>Giocatore</th>
                <th>Rank / MMR</th>
                <th>Partite</th>
                <th>V / S / P</th>
                <th>Win rate</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.uuid}>
                  <td className="table-position">#{entry.rankPosition}</td>
                  <td>
                    <Link
                      className="player-cell"
                      to={`/player/${entry.uuid}`}
                    >
                      <PlayerAvatar
                        size={40}
                        username={entry.username}
                        uuid={entry.uuid}
                      />
                      <strong>{entry.username}</strong>
                    </Link>
                  </td>
                  <td>
                    <RankBadge rank={entry.rank} />
                    <span className="table-sub">{number(entry.mmr)} MMR</span>
                  </td>
                  <td>{number(entry.matchesPlayed)}</td>
                  <td>
                    {entry.wins} / {entry.losses} / {entry.draws}
                  </td>
                  <td>{percentage(entry.winRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            onPage={(nextPage) =>
              setParams(nextPage === 1 ? {} : { page: String(nextPage) })
            }
            pagination={data.pagination}
          />
        </section>
      ) : null}
    </main>
  )
}
